import React, { useState, useEffect, useMemo } from "react";
import {
  Plus,
  Activity,
  Cpu,
  CheckCircle2,
  FastForward,
  Users,
  AlertCircle,
  Info,
} from "lucide-react";
import { useLocation } from "react-router-dom";

const Monitor = () => {
  const [sites, setSites] = useState([]);
  const [liveMap, setLiveMap] = useState({});
  const [verifiedMap, setVerifiedMap] = useState({});
  const [calvesMap, setCalvesMap] = useState({});
  const [selectedSite, setSelectedSite] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newSiteName, setNewSiteName] = useState("");
  const [newSiteMac, setNewSiteMac] = useState("");
  const [saving, setSaving] = useState(false);
  const userRole = localStorage.getItem("role");
  const userId = localStorage.getItem("id");
  const [statusFilter, setStatusFilter] = useState(null); // 'online', 'offline', or null
  const isSiteOnline = (lastHeartbeat) => {
    if (!lastHeartbeat) return false;
    const time = new Date(lastHeartbeat.replace(" ", "T") + "Z");
    const diff = (Date.now() - time.getTime()) / 1000;
    return diff <= 60;
  };

  // --- Calculate Totals ---
  const statsSummary = useMemo(() => {
    const online = sites.filter((s) => isSiteOnline(s.last_heartbeat)).length;
    const offline = sites.length - online;
    return { online, offline };
  }, [sites]);

  const filteredSites = useMemo(() => {
    if (!statusFilter) return sites;
    return sites.filter((site) => {
      const online = isSiteOnline(site.last_heartbeat);
      return statusFilter === "online" ? online : !online;
    });
  }, [sites, statusFilter]);
  const [modeMap, setModeMap] = useState({});
  const pollCurrentMode = async (site) => {
    try {
      console.log(site);
      const res = await fetch(
        `https://db2sg.com/tagweigh/api/mode?siteMac=${
          site.mac
        }&t=${Date.now()}`
      );

      const data = await res.json();

      if (!data) return;
      console.log(data.data);
      setModeMap((prev) => ({
        ...prev,
        [site.mac]: {
          mode: data.data.mode || "Unknown",
          status: data.data.status || "stop", // ✅ start/stop
        },
      }));
    } catch (e) {
      console.error("Failed to fetch mode", site.mac, e);
    }
  };

  // --- Fetch all sites ---
  const fetchSites = async () => {
    try {
      const res = await fetch(
        `https://db2sg.com/tagweigh/api/sites?_t=${Date.now()}`
      );
      const data = await res.json();
      let filteredSites = data || [];
      if (userRole === "user") {
        filteredSites = filteredSites.filter(
          (site) => site.user?.toString() === userId?.toString()
        );
      }
      setSites(filteredSites);
      // setSites(data || []);
    } catch (e) {
      console.error("Failed to fetch sites", e);
    }
  };

  // --- Fetch calves info ---
  const fetchCalves = async () => {
    try {
      const res = await fetch("https://db2sg.com/tagweigh/api/calves");
      const data = await res.json();
      const map = {};
      (data.sites || []).forEach((s) => {
        map[s.mac] = s.calvesCount || 0;
      });
      setCalvesMap(map);
    } catch (e) {
      console.error("Failed to fetch calves", e);
    }
  };

  // --- Fetch verified/skipped stats for each site ---
  const fetchWeighingStats = async (siteMac) => {
    try {
      const res = await fetch(
        `https://db2sg.com/tagweigh/api/weighings?siteMac=${siteMac}&offset=0`
      );
      const data = await res.json();
      const today = new Date().toISOString().split("T")[0];

      const todayWeighings = (data.weighings || []).filter(
        (w) => w.weight_date === today
      );
      const verified =
        data.weighings?.filter((w) => w.validated === 1 || w.validated === "1")
          .length || 0;
      const skipped =
        data.weighings?.filter((w) => w.mode === "skipped").length || 0; // assuming 2 = skipped
      const total = todayWeighings?.length || 0;
      setVerifiedMap((prev) => ({
        ...prev,
        [siteMac]: { total, verified, skipped },
      }));
    } catch (e) {
      console.error("Failed to fetch weighings", siteMac, e);
    }
  };

  // --- Poll live run state ---
  const pollRunMode = async (site) => {
    try {
      const res = await fetch(
        `https://db2sg.com/tagweigh/api/run_mode?siteMac=${
          site.mac
        }&_t=${Date.now()}`
      );
      const data = await res.json();
      console.log(data);
      if (!data) return;

      let status = "Ready";
      let errorType: any = null;

      if (
        data.rfid_status === "ready" ||
        data.rfid_status === "completed" ||
        data.rfid_status === "complete" ||
        data?.rfid_status === "system_ready" ||
        data?.rfid_status === "weigh_ok"
      ) {
        status = "Ready";
      } else if (
        data.rfid_status === "processing" ||
        data.rfid_status === "Processing_rfid"
      ) {
        status = "Processing";
      }
      else if (data.rfid_status === "id_fail") {
        status = "Error";
        errorType = data?.rfid_message;
      } else if (data.rfid_status === "weigh_fail") {
        status = "Error";
        errorType = data?.weigh_message;
      }
      console.log({
        lastCycleTag: data.rfid || "---",
        lastCycleWeight: data.weight || "---",
        updatedAt: data.updatedAt,
      });
      setLiveMap((prev) => {
        const newMap = { ...prev };
        newMap[site.mac] = {
          status,
          errorType,
          lastCycleTag: data.rfid || "---",
          lastCycleWeight: data.weight || "---",
          updatedAt: data.updatedAt,
        };
        return newMap;
      });
      // setLiveMap((prev) => ({
      //   ...prev,
      //   [site.mac]: {
      //     status,
      //     errorType,
      //     lastCycleTag: data.rfid || "---",
      //     lastCycleWeight: data.weight || "---",
      //     updatedAt: data.updatedAt,
      //   },
      // }));
    } catch (e) {
      console.error("Failed to poll run_mode", site.mac, e);
    }
  };

  // --- Determine online/offline based on last heartbeat ---
  const getOnlineStatus = (lastHeartbeat) => {
    if (!lastHeartbeat) return false;

    // FORCE UTC like mobile
    const time = new Date(lastHeartbeat.replace(" ", "T") + "Z");
    const diff = (Date.now() - time.getTime()) / 1000;

    return diff <= 60;
  };
  // useEffect(() => {
  //   fetchSites();

  //   // eslint-disable-next-line
  // }, []);
  useEffect(() => {
    fetchSites(); // first load
    fetchCalves();
    const interval = setInterval(() => {
      fetchSites(); // refresh heartbeat
    }, 20000); // 20 seconds

    return () => clearInterval(interval);
    // eslint-disable-next-line
  }, []);
  const location = useLocation();

  useEffect(() => {
    if (location.state?.status) {
      setStatusFilter(location.state.status); // "online" or "offline"
    }
  }, [location.state]);

  // Fetch stats when sites are loaded
  useEffect(() => {
    if (!sites.length) return;
    sites.forEach((site) => {
      fetchWeighingStats(site.mac);
      pollRunMode(site);
      pollCurrentMode(site); // ✅ NEW
    });

    // Poll run_mode every 2 seconds
    const interval = setInterval(() => {
      // sites.forEach(pollRunMode);
      // pollCurrentMode(site); // ✅ NEW
      sites.forEach((site) => {
        pollRunMode(site);
        pollCurrentMode(site); // ✅ NEW
      });
    }, 2000);
    return () => clearInterval(interval);
  }, [sites]);
  const handleAddSite = async () => {
    if (!newSiteName || !newSiteMac) {
      alert("נא למלא שם ו-MAC");
      return;
    }

    try {
      setSaving(true);

      const response = await fetch("https://db2sg.com/tagweigh/api/sites", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          // Authorization: `Bearer ${token}`, // make sure token exists
        },
        body: JSON.stringify({
          mac: newSiteMac,
          ios_mac: "",
          name: newSiteName,
          location: "",
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to add site");
      }

      // refresh sites list
      await fetchSites();

      // reset & close
      setNewSiteName("");
      setNewSiteMac("");
      setShowAddModal(false);
    } catch (e) {
      console.error("Add site failed", e);
      alert("שגיאה בהוספת אתר");
    } finally {
      setSaving(false);
    }
  };
  const EmptyState = () => (
    <div className="flex flex-col items-center justify-center py-20 px-6 bg-white rounded-[3rem] border-2 border-dashed border-slate-200 shadow-sm animate-in fade-in zoom-in duration-500">
      <div className="w-20 h-20 bg-amber-50 rounded-full flex items-center justify-center text-amber-500 mb-6">
        <AlertCircle size={40} />
      </div>
      <h3 className="text-2xl font-black text-slate-900 mb-2">
        לא נמצאו אתרים משוייכים
      </h3>
      <p className="text-slate-500 font-bold text-center max-w-sm mb-8">
        נראה שעדיין לא הוגדרו אתרים לחשבון שלך.
        <br />
        אנא צור קשר עם הטכנאי שלך להוספת האתר למערכת.
      </p>
      <div className="flex items-center gap-2 text-blue-600 bg-blue-50 px-4 py-2 rounded-xl font-black text-xs">
        <Info size={14} />
        <span>תמיכה טכנית זמינה עבורך</span>
      </div>
    </div>
  );
  const toggleFilter = (type) => {
    if (statusFilter === type) {
      setStatusFilter(null); // Clear filter if clicking the same card
    } else {
      setStatusFilter(type);
    }
  };
  return (
    <div className="animate-in fade-in slide-in-from-bottom-8 duration-1000 pb-20 ltr">
      <div className="grid grid-cols-3 md:grid-cols-3 gap-4 mb-8">
        {userRole !== "user" && (
          <div className="flex flex-col md:items-center gap-0 mb-10">
            <button
              onClick={() => setShowAddModal(true)}
              className="flex items-center bg-slate-900 text-white px-8 py-4 rounded-2xl font-black uppercase tracking-[2px] hover:bg-blue-600 transition-all shadow-xl"
            >
              <Plus size={20} /> Add New Site
            </button>
          </div>
        )}
        <button
          onClick={() => toggleFilter("online")}
          className={`p-6 rounded-[1rem] border transition-all duration-300 flex items-center gap-4 group ${
            statusFilter === "online"
              ? "bg-emerald-500 border-emerald-600 text-white shadow-lg shadow-emerald-200 scale-105"
              : "bg-white border-slate-100 text-slate-600 hover:border-emerald-200"
          }`}
        >
          <div
            className={`p-4 rounded-2xl shrink-0 ${
              statusFilter === "online"
                ? "bg-white/20"
                : "bg-emerald-50 text-emerald-500"
            }`}
          >
            <Activity size={24} />
          </div>
          <div className="flex flex-col text-center leading-tight">
            <span
              className={`text-3xl font-black ${
                statusFilter === "online" ? "text-white" : "text-slate-900"
              }`}
            >
              {statsSummary.online}
            </span>
            <span className="text-[11px] font-black uppercase tracking-widest opacity-70">
              אתרים מחוברים
            </span>
          </div>
        </button>

        <button
          onClick={() => toggleFilter("offline")}
          className={`p-6 rounded-[1rem] border transition-all duration-300 flex items-center gap-4 group ${
            statusFilter === "offline"
              ? "bg-red-500 border-red-600 text-white shadow-lg shadow-red-200 scale-105"
              : "bg-white border-slate-100 text-slate-600 hover:border-red-200"
          }`}
        >
          <div
            className={`p-4 rounded-2xl shrink-0 ${
              statusFilter === "offline"
                ? "bg-white/20"
                : "bg-red-50 text-red-500"
            }`}
          >
            <AlertCircle size={24} />
          </div>
          <div className="flex flex-col text-center leading-tight">
            <span
              className={`text-3xl font-black ${
                statusFilter === "offline" ? "text-white" : "text-slate-900"
              }`}
            >
              {statsSummary.offline}
            </span>
            <span className="text-[11px] font-black uppercase tracking-widest opacity-70">
              אתרים לא מחוברים
            </span>
          </div>
        </button>
      </div>
      {statusFilter && (
        <div className="mb-6 flex items-center justify-between bg-blue-50 px-6 py-3 rounded-2xl border border-blue-100">
          <p className="text-blue-700 font-bold text-sm">
            מציג כרגע:{" "}
            <span className="underline">
              {statusFilter === "online" ? "אתרים מחוברים" : "אתרים לא מחוברים"}
            </span>
          </p>
          <button
            onClick={() => setStatusFilter(null)}
            className="text-xs font-black text-blue-800 bg-white px-3 py-1 rounded-lg shadow-sm"
          >
            ביטול סינון ✕
          </button>
        </div>
      )}
      {filteredSites.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {filteredSites.map((site) => {
            const live = liveMap[site.mac] || {};
            const stats = verifiedMap[site.mac] || {};
            const calves = calvesMap[site.mac] || 0;
            const online = getOnlineStatus(site.last_heartbeat);
            const modeInfo = modeMap[site.mac] || {};
            const mode = modeInfo.mode || "";
            const runStatus = modeInfo.status || "stop";

            return (
              <div
              key={`${site.mac}-${live.updatedAt || 'no-date'}-${online}-${mode}`}
                                            className="bg-white rounded-[2rem] border border-slate-100 shadow-xl overflow-hidden flex flex-col"
              >
                <div className="p-7 pb-3 flex justify-between items-start gap-4">
                  {/* Left Side: Icon and Text */}
                  <div className="flex gap-4 min-w-0 flex-1">
                    <div className="w-14 h-14 bg-slate-50 rounded-2xl flex items-center justify-center text-slate-400 shrink-0">
                      <Cpu size={28} />
                    </div>

                    <div className="min-w-0 flex-1">
                      <h3 className="text-xl font-black text-slate-900 truncate">
                        {site.name}
                      </h3>

                      <code className="text-[11px] font-mono font-medium text-slate-600 px-0 py-0.5 rounded block truncate">
                        {site.mac}
                      </code>
                    </div>
                  </div>

                  {/* Right Side: Traffic Light - Added shrink-0 to keep its shape */}
                  <div className="flex flex-col gap-1.5 p-2 bg-slate-900 rounded-full border border-white/5 shrink-0">
                    <div
                      className={`w-3 h-3 rounded-full ${
                        live.status === "Error"
                          ? "bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.8)]"
                          : "bg-red-950"
                      }`}
                    />
                    <div
                      className={`w-3 h-3 rounded-full ${
                        live.status === "Processing"
                          ? "bg-yellow-400 shadow-[0_0_8px_rgba(250,204,21,0.8)]"
                          : "bg-yellow-950"
                      }`}
                    />
                    <div
                      className={`w-3 h-3 rounded-full ${
                        live.status === "Ready"
                          ? "bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.8)]"
                          : "bg-emerald-950"
                      }`}
                    />
                  </div>
                </div>

                <div className="px-8 flex gap-2 mb-3">
                  <span
                    className={`px-3 py-1 rounded-lg text-[10px] font-black flex items-center gap-1.5 ${
                      calves > 0
                        ? "bg-slate-100 text-slate-700"
                        : "bg-slate-100 text-slate-500"
                    }`}
                  >
                    Last Updated:{live.updatedAt}
                  </span>
                </div>

                {/* Last Cycle Info */}
                {/* Error Indicator */}
                {live.status === "Error" && (
                  <div className="mx-8 mb-5 overflow-hidden relative">
                    {/* Background Glow Animation */}
                    <div className="absolute inset-0 bg-red-500/10 animate-pulse rounded-2xl" />

                    <div className="relative flex items-center gap-4 bg-white border-2 border-red-100 shadow-[0_8px_20px_-6px_rgba(239,68,68,0.2)] rounded-2xl px-5 py-4">
                      {/* Icon with Ring Effect */}
                      <div className="relative flex shrink-0">
                        <div className="absolute inset-0 bg-red-500 rounded-full animate-ping opacity-20" />
                        <div className="relative bg-red-500 text-white p-2.5 rounded-xl shadow-lg shadow-red-200">
                          <AlertCircle size={20} strokeWidth={3} />
                        </div>
                      </div>

                      {/* Text Content */}
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-black text-red-700 truncate italic">
                          {live.errorType}
                        </h4>
                      </div>
                    </div>
                  </div>
                )}

                <div className="px-8 mb-3 grid grid-cols-2 gap-3">
                  {/* RFID */}
                  <div
                    className="bg-slate-50 rounded-2xl p-3 border text-center"
                  >
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
                      Last RFID
                    </p>
                    <p className="font-mono font-black text-slate-800 text-sm truncate">
                      {live.lastCycleTag?.replaceAll("R_HDX_", "")?.replaceAll("R_FDX_", "") || "---"}
                    </p>
                  </div>

                  {/* Weight */}
                  <div
                    className="bg-slate-50 rounded-2xl p-3 border text-center"
                  >
                    <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
                      Last Weight
                    </p>
                    <p className="font-black text-slate-800 text-sm">
                      {live.lastCycleWeight || "---"}{" "}
                      <span className="text-[10px] text-slate-400">kg</span>
                    </p>
                  </div>
                </div>
                <div className="px-8 flex gap-2 mb-2">
                  <span
                    className={`px-3 py-1 rounded-lg text-[10px] font-black flex items-center gap-1.5 ${
                      online
                        ? "bg-emerald-100 text-emerald-700"
                        : "bg-red-100 text-red-700"
                    }`}
                  >
                    {online ? "🟢 מחובר" : "🔴 לא מחובר"}
                  </span>
                  <span
                    className={`px-3 py-1 rounded-lg text-[10px] font-black flex items-center gap-1.5 ${
                      calves > 0
                        ? "bg-blue-100 text-blue-700"
                        : "bg-slate-100 text-slate-500"
                    }`}
                  >
                    📚 {calves > 0 ? "מאגר פעיל" : "ללא מאגר"}
                  </span>
                  <span
                    className={`px-3 py-1 rounded-lg text-[10px] font-black flex items-center gap-1.5 ${
                      (mode === "run" && runStatus === "start") || mode === "test"
                        ? "bg-green-100 text-green-700"
                        : "bg-red-100 text-red-500"
                    }`}
                  >
                    ⚙️ Mode: {mode}
                  </span>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-4 gap-2 px-6 mb-0">
                  <StatItem
                    label="שקילות היום"
                    value={stats.total}
                    icon={<Activity size={12} />}
                  />
                  <StatItem
                    label="מאומתים"
                    value={stats.verified}
                    icon={<CheckCircle2 size={12} />}
                    color="text-emerald-500"
                  />
                  <StatItem
                    label="דילוגים"
                    value={stats.skipped}
                    icon={<FastForward size={12} />}
                    color="text-amber-500"
                  />
                  <StatItem
                    label="עגלים במאגר"
                    value={calves}
                    icon={<Users size={12} />}
                    color="text-blue-500"
                  />
                </div>

                {/* Footer */}
                {/* <div className="p-6 bg-slate-50/50 border-t border-slate-50 mt-auto">
                  <button
                    onClick={() => setSelectedSite(site)}
                    className="w-full flex items-center justify-center gap-2 bg-white border border-slate-200 py-3 rounded-2xl font-black text-xs text-slate-600 hover:bg-slate-900 hover:text-white hover:border-slate-900 transition-all shadow-sm"
                  >
                    <Eye size={16} /> צפה בפרטי אתר
                  </button>
                </div> */}
              </div>
            );
          })}
        </div>
      )}
      {selectedSite && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-xl shadow-2xl relative animate-in fade-in zoom-in">
            {/* Header */}
            <div className="p-6 border-b flex justify-between items-center">
              <h3 className="text-xl font-black text-slate-900">🖥️ פרטי אתר</h3>
              <button
                onClick={() => setSelectedSite(null)}
                className="text-slate-400 hover:text-slate-900 font-black text-lg"
              >
                ✕
              </button>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4 text-sm">
              <Detail label="שם אתר" value={selectedSite.name} />
              <Detail label="MAC" value={selectedSite.mac} />
              <Detail
                label="סטטוס"
                value={
                  getOnlineStatus(selectedSite.last_heartbeat)
                    ? "מחובר"
                    : "לא מחובר"
                }
              />
              <Detail
                label="Heartbeat אחרון"
                value={selectedSite.last_heartbeat || "--"}
              />

              <Detail
                label="עגלים במאגר"
                value={calvesMap[selectedSite.mac] || 0}
              />

              <Detail
                label="שקילות היום"
                value={verifiedMap[selectedSite.mac]?.total || 0}
              />
            </div>

            {/* Footer */}
            <div className="p-6 border-t bg-slate-50">
              <button
                onClick={() => setSelectedSite(null)}
                className="w-full bg-slate-900 text-white py-3 rounded-xl font-black hover:bg-blue-600 transition"
              >
                סגור
              </button>
            </div>
          </div>
        </div>
      )}
      {showAddModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-md shadow-2xl animate-in fade-in zoom-in">
            {/* Header */}
            <div className="p-6 border-b flex justify-between items-center">
              <h3 className="text-xl font-black text-slate-900">
                ➕ הוספת אתר חדש
              </h3>
              <button
                onClick={() => setShowAddModal(false)}
                className="text-slate-400 hover:text-slate-900 font-black text-lg"
              >
                ✕
              </button>
            </div>

            {/* Form */}
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">
                  שם האתר
                </label>
                <input
                  value={newSiteName}
                  onChange={(e) => setNewSiteName(e.target.value)}
                  className="w-full border rounded-xl px-4 py-3 font-bold focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="לדוגמה: אתר צפון"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">
                  MAC Address
                </label>
                <input
                  value={newSiteMac}
                  onChange={(e) => setNewSiteMac(e.target.value)}
                  className="w-full border rounded-xl px-4 py-3 font-mono font-black focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="40:22:D8:EA:61:32"
                />
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 border-t bg-slate-50">
              <button
                disabled={saving}
                onClick={handleAddSite}
                className="w-full bg-slate-900 text-white py-3 rounded-xl font-black hover:bg-blue-600 transition disabled:opacity-50"
              >
                {saving ? "שומר..." : "שמור אתר"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
const Detail = ({ label, value }) => (
  <div className="flex justify-between items-center bg-slate-50 px-4 py-3 rounded-xl">
    <span className="font-bold text-slate-500">{label}</span>
    <span className="font-mono font-black text-slate-800">{value}</span>
  </div>
);

const StatItem = ({ label, value, icon, color = "text-slate-400" }) => (
  <div className="bg-white border border-slate-50 rounded-2xl p-3 flex flex-col items-center text-center shadow-sm">
    <div className={`mb-1 ${color}`}>{icon}</div>
    <div className="text-lg font-black text-slate-800 leading-none">
      {value || 0}
    </div>
    <div className="text-[8px] font-bold text-slate-400 uppercase tracking-tighter mt-1 leading-tight">
      {label}
    </div>
  </div>
);

export default Monitor;
